import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, TrendingDown, Calendar, Percent } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';

export const OverdraftCalculator = () => {
  const [overdraftLimit, setOverdraftLimit] = useState(500000);
  const [utilizationAmount, setUtilizationAmount] = useState(300000);
  const [utilizationDays, setUtilizationDays] = useState(180);
  const [interestRate, setInterestRate] = useState(12);
  
  const [costs, setCosts] = useState({
    interestCost: 0,
    dailyInterest: 0,
    monthlyInterest: 0,
    yearlyInterest: 0
  });

  const calculateCosts = useCallback(() => {
    // Interest is charged only on utilized amount
    const dailyRate = (interestRate / 365) / 100;
    const dailyInterest = utilizationAmount * dailyRate;
    const totalInterest = dailyInterest * utilizationDays;
    const monthlyInterest = (utilizationAmount * (interestRate / 12) / 100);
    const yearlyInterest = (utilizationAmount * (interestRate / 100));
    
    setCosts({
      interestCost: totalInterest,
      dailyInterest: dailyInterest,
      monthlyInterest: monthlyInterest,
      yearlyInterest: yearlyInterest
    });
  }, [overdraftLimit, utilizationAmount, utilizationDays, interestRate]);

  useEffect(() => {
    calculateCosts();
  }, [calculateCosts]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const utilizationPercent = ((utilizationAmount / overdraftLimit) * 100).toFixed(1);

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Overdraft <span className="text-emerald-600">Calculator</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Calculate interest costs on your overdraft facility
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Input Section */}
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-6 h-6 text-emerald-600" />
                Overdraft Details
              </CardTitle>
              <CardDescription>Enter your overdraft facility details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Overdraft Limit */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Overdraft Limit</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(overdraftLimit)}</span>
                </div>
                <Slider
                  value={[overdraftLimit]}
                  onValueChange={(value) => setOverdraftLimit(value[0])}
                  min={100000}
                  max={10000000}
                  step={100000}
                  className="w-full"
                />
              </div>

              {/* Utilization Amount */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Utilized Amount</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(utilizationAmount)}</span>
                </div>
                <Slider
                  value={[utilizationAmount]}
                  onValueChange={(value) => setUtilizationAmount(value[0])}
                  min={0}
                  max={overdraftLimit}
                  step={10000}
                  className="w-full"
                />
                <div className="text-sm text-slate-600">
                  Utilization: {utilizationPercent}% of limit
                </div>
              </div>

              {/* Utilization Days */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Utilization Period</Label>
                  <span className="text-xl font-bold text-emerald-600">{utilizationDays} days</span>
                </div>
                <Slider
                  value={[utilizationDays]}
                  onValueChange={(value) => setUtilizationDays(value[0])}
                  min={1}
                  max={365}
                  step={1}
                  className="w-full"
                />
              </div>

              {/* Interest Rate */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Interest Rate (p.a.)</Label>
                  <span className="text-xl font-bold text-emerald-600">{interestRate.toFixed(1)}%</span>
                </div>
                <Slider
                  value={[interestRate]}
                  onValueChange={(value) => setInterestRate(value[0])}
                  min={8}
                  max={20}
                  step={0.1}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <div className="space-y-6">
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white">
              <CardHeader>
                <CardTitle className="text-emerald-700">Interest Costs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-white p-6 rounded-xl border-2 border-emerald-200 text-center">
                  <p className="text-sm text-slate-600 mb-2">Total Interest Cost</p>
                  <p className="text-4xl font-bold text-emerald-600">
                    {formatCurrency(costs.interestCost)}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">for {utilizationDays} days</p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Daily Interest</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(costs.dailyInterest)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Monthly Interest</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(costs.monthlyInterest)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Yearly Interest</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(costs.yearlyInterest)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
              <CardContent className="pt-6">
                <Percent className="w-8 h-8 mb-2" />
                <p className="text-sm opacity-90 mb-1">Available Credit</p>
                <p className="text-2xl font-bold">{formatCurrency(overdraftLimit - utilizationAmount)}</p>
                <p className="text-xs opacity-75 mt-2">Unused overdraft facility</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
